/** Icon and Graphic: the kit's line art, rendered as images so nothing inside them is hit-tested, selected or restyled by the page. */
import { cx } from "../cx";
import { paint, type Recipe } from "./paint";
import { BRAND_PAINT, type Colour } from "./palette";
import { GRAPHICS, ICONS } from "./set";

export type IconName = keyof typeof ICONS;
export type GraphicName = keyof typeof GRAPHICS;

const sources = new Map<Recipe, string>();
const brandSources = new Map<Recipe, string>();

const recolour = (colour: Colour | undefined) => (colour && BRAND_PAINT[colour]) ?? colour;

/** The recipe in the brand paint: its neutral ink and fills swapped for the brand hue and its tints. */
const branded = (recipe: Recipe): Recipe => ({ ...recipe, layers: recipe.layers.map((layer) => ({ ...layer, fill: recolour(layer.fill), stroke: recolour(layer.stroke) })) });

function source(recipe: Recipe, brand = false): string {
  const cache = brand ? brandSources : sources;
  let src = cache.get(recipe);
  if (!src) {
    src = paint(brand ? branded(recipe) : recipe);
    cache.set(recipe, src);
  }
  return src;
}

/**
 * A square icon. `size` is its side in pixels (16 unless given). A `fluid` icon takes its size from
 * `className` instead (`size-3`), so it follows the root font size. `brand` paints it in the brand hue
 * (the active nav row, a KPI well).
 */
export function Icon({ name, size = 16, fluid = false, brand = false, className }: { name: IconName; size?: number; fluid?: boolean; brand?: boolean; className?: string }) {
  return <img src={source(ICONS[name], brand)} data-paint={brand ? "brand" : undefined} width={size} height={size} alt="" className={cx("block shrink-0", className)} style={fluid ? undefined : { width: size, height: size }} />;
}

/** A piece of line art at its own size; `className` places and sizes it. */
export function Graphic({ name, className }: { name: GraphicName; className?: string }) {
  const recipe = GRAPHICS[name];
  return <img src={source(recipe)} alt="" width={recipe.w} height={recipe.h} className={className} />;
}
